import React, { createContext, useState, useMemo } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const themes = {
  light: {
    palette: {
      primary: { main: "#635bff" },
      secondary: { main: "#ff4081" },
      error: { main: "#f44336" },
      background: { default: "#f5f5f5" },
      text: { primary: "#333333", secondary: "#757575" },
    },
    cssVars: {
      "--primary-color": "#635bff",
      "--primary-gradient": "linear-gradient(90deg, #BB86FC, #3700B3)",
      "--secondary-color": "#ff4081",
      "--accent-color-1": "#ffb74d",
      "--accent-color-2": "#66bb6a",
      "--background-color": "#f5f5f5",
      "--sidebar-bg-color": "#121621",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #121621, #121352)",
      "--paper-color": "#ffffff",
      "--text-primary-color": "#ffffff",
      "--text-secondary-color": "#757575",
      "--button-text-transform": "none",
      "--error-color": "#f44336",
      "--button-hover-color": "#4e4abf",
      "--primary-light-color": "#e2e0fc",
    },
  },
  dark: {
    palette: {
      primary: { main: "#3700B3" },
      secondary: { main: "#ff4081" },
      error: { main: "#cf6679" },
      background: { default: "#121212" },
      text: { primary: "#ffffff", secondary: "#bbbbbb" },
    },
    cssVars: {
      "--primary-color": "#3700B3",
      "--primary-gradient": "linear-gradient(90deg, #121621, #635bff)",
      "--secondary-color": "#ff4081",
      "--accent-color-1": "#ff8c42",
      "--accent-color-2": "#8bc34a",
      "--background-color": "#121212",
      "--sidebar-bg-color": "#1e1e1e",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #1e1e1e, #272727)",
      "--paper-color": "#1e1e1e",
      "--text-primary-color": "#ffffff",
      "--text-secondary-color": "#bbbbbb",
      "--button-text-transform": "uppercase",
      "--error-color": "#cf6679",
      "--button-hover-color": "#635bff",
      "--primary-light-color": "#453f91",
    },
  },
  highContrast: {
    palette: {
      primary: { main: "#000000" },
      secondary: { main: "#ffff00" },
      error: { main: "#d32f2f" },
      background: { default: "#ffffff" },
      text: { primary: "#000000", secondary: "#333333" },
    },
    cssVars: {
      "--primary-color": "#000000",
      "--primary-gradient": "linear-gradient(90deg, #000000, #444444)",
      "--secondary-color": "#ffff00",
      "--accent-color-1": "#ff5722",
      "--accent-color-2": "#4caf50",
      "--background-color": "#ffffff",
      "--sidebar-bg-color": "#ffffff",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #ffffff, #eeeeee)",
      "--paper-color": "#f5f5f5",
      "--text-primary-color": "#000000",
      "--text-secondary-color": "#333333",
      "--button-text-transform": "capitalize",
      "--error-color": "#d32f2f",
      "--button-hover-color": "#212121",
      "--primary-light-color": "#eeeeee",
    },
  },
  oceanBreeze: {
    palette: {
      primary: { main: "#1e88e5" }, // Blue
      secondary: { main: "#4caf50" }, // Green
      error: { main: "#d32f2f" }, // Red
      background: { default: "#e0f7fa" }, // Light Blue
      text: { primary: "#212121", secondary: "#757575" },
    },
    cssVars: {
      "--primary-color": "#1e88e5",
      "--primary-gradient": "linear-gradient(90deg, #4caf50, #1e88e5)",
      "--secondary-color": "#4caf50",
      "--accent-color-1": "#80e27e",
      "--accent-color-2": "#81d4fa",
      "--background-color": "#e0f7fa",
      "--sidebar-bg-color": "#ffffff",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #ffffff, #b3e5fc)",
      "--paper-color": "#ffffff",
      "--text-primary-color": "#212121",
      "--text-secondary-color": "#757575",
      "--button-text-transform": "none",
      "--error-color": "#d32f2f",
      "--button-hover-color": "#0288d1",
      "--primary-light-color": "#81d4fa",
    },
  },
  sunsetGlow: {
    palette: {
      primary: { main: "#ff7043" }, // Orange
      secondary: { main: "#f50057" }, // Pink
      error: { main: "#d32f2f" }, // Red
      background: { default: "#fff3e0" }, // Light Yellow
      text: { primary: "#212121", secondary: "#757575" },
    },
    cssVars: {
      "--primary-color": "#ff7043",
      "--primary-gradient": "linear-gradient(90deg, #ff7043, #f50057)",
      "--secondary-color": "#f50057",
      "--accent-color-1": "#ff4081",
      "--accent-color-2": "#ba68c8",
      "--background-color": "#fff3e0",
      "--sidebar-bg-color": "#ffffff",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #ffffff, #ffccbc)",
      "--paper-color": "#ffffff",
      "--text-primary-color": "#212121",
      "--text-secondary-color": "#757575",
      "--button-text-transform": "uppercase",
      "--error-color": "#d32f2f",
      "--button-hover-color": "#e64a19",
      "--primary-light-color": "#ffab91",
    },
  },
  forestRetreat: {
    palette: {
      primary: { main: "#388e3c" }, // Green
      secondary: { main: "#8d6e63" }, // Brown
      error: { main: "#d32f2f" }, // Red
      background: { default: "#f1f8e9" }, // Light Green
      text: { primary: "#212121", secondary: "#757575" },
    },
    cssVars: {
      "--primary-color": "#388e3c",
      "--primary-gradient": "linear-gradient(90deg, #388e3c, #8d6e63)",
      "--secondary-color": "#8d6e63",
      "--accent-color-1": "#81c784",
      "--accent-color-2": "#c8e6c9",
      "--background-color": "#f1f8e9",
      "--sidebar-bg-color": "#ffffff",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #ffffff, #c5e1a5)",
      "--paper-color": "#ffffff",
      "--text-primary-color": "#212121",
      "--text-secondary-color": "#757575",
      "--button-text-transform": "none",
      "--error-color": "#d32f2f",
      "--button-hover-color": "#2c6e1f",
      "--primary-light-color": "#a5d6a7",
    },
  },
  lavenderDream: {
    palette: {
      primary: { main: "#7e57c2" }, // Lavender
      secondary: { main: "#8e24aa" }, // Purple
      error: { main: "#c2185b" }, // Pinkish Red
      background: { default: "#f3e5f5" }, // Light Lavender
      text: { primary: "#4527a0", secondary: "#7b1fa2" },
    },
    cssVars: {
      "--primary-color": "#7e57c2",
      "--primary-gradient": "linear-gradient(90deg, #7e57c2, #8e24aa)",
      "--secondary-color": "#8e24aa",
      "--accent-color-1": "#9575cd",
      "--accent-color-2": "#d1c4e9",
      "--background-color": "#f3e5f5",
      "--sidebar-bg-color": "#ffffff",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #ffffff, #e1bee7)",
      "--paper-color": "#ffffff",
      "--text-primary-color": "#4527a0",
      "--text-secondary-color": "#7b1fa2",
      "--button-text-transform": "none",
      "--error-color": "#c2185b",
      "--button-hover-color": "#6a1b9a",
      "--primary-light-color": "#b39ddb",
    },
  },
  autumnHarvest: {
    palette: {
      primary: { main: "#e65100" }, // Dark Amber
      secondary: { main: "#f57c00" }, // Orange
      error: { main: "#b71c1c" }, // Dark Red
      background: { default: "#fff8e1" }, // Soft Yellow
      text: { primary: "#3e2723", secondary: "#5d4037" },
    },
    cssVars: {
      "--primary-color": "#e65100",
      "--primary-gradient": "linear-gradient(90deg, #e65100, #f57c00)",
      "--secondary-color": "#f57c00",
      "--accent-color-1": "#ffb74d",
      "--accent-color-2": "#ffcc80",
      "--background-color": "#fff8e1",
      "--sidebar-bg-color": "#ffffff",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #ffffff, #ffecb3)",
      "--paper-color": "#ffffff",
      "--text-primary-color": "#3e2723",
      "--text-secondary-color": "#5d4037",
      "--button-text-transform": "none",
      "--error-color": "#b71c1c",
      "--button-hover-color": "#bf360c",
      "--primary-light-color": "#ff7043",
    },
  },
  sunsetGlow: {
    palette: {
      primary: { main: "#ff5722" }, // Deep Orange
      secondary: { main: "#ff9800" }, // Amber
      error: { main: "#d32f2f" }, // Red
      background: { default: "#fff3e0" }, // Soft Peach
      text: { primary: "#e64a19", secondary: "#ff6f00" },
    },
    cssVars: {
      "--primary-color": "#ff5722",
      "--primary-gradient": "linear-gradient(90deg, #ff5722, #ff9800)",
      "--secondary-color": "#ff9800",
      "--accent-color-1": "#ffccbc",
      "--accent-color-2": "#ffe0b2",
      "--background-color": "#fff3e0",
      "--sidebar-bg-color": "#ffffff",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #ffffff, #ffebee)",
      "--paper-color": "#ffffff",
      "--text-primary-color": "#e64a19",
      "--text-secondary-color": "#ff6f00",
      "--button-text-transform": "none",
      "--error-color": "#d32f2f",
      "--button-hover-color": "#e64a19",
      "--primary-light-color": "#ff8a65",
    },
  },
  oceanBreeze: {
    palette: {
      primary: { main: "#0288d1" }, // Ocean Blue
      secondary: { main: "#00796b" }, // Sea Green
      error: { main: "#c62828" }, // Red
      background: { default: "#e1f5fe" }, // Light Blue
      text: { primary: "#01579b", secondary: "#0288d1" },
    },
    cssVars: {
      "--primary-color": "#0288d1",
      "--primary-gradient": "linear-gradient(90deg, #0288d1, #00796b)",
      "--secondary-color": "#00796b",
      "--accent-color-1": "#81d4fa",
      "--accent-color-2": "#b3e5fc",
      "--background-color": "#e1f5fe",
      "--sidebar-bg-color": "#ffffff",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #ffffff, #80deea)",
      "--paper-color": "#ffffff",
      "--text-primary-color": "#01579b",
      "--text-secondary-color": "#0288d1",
      "--button-text-transform": "none",
      "--error-color": "#c62828",
      "--button-hover-color": "#01579b",
      "--primary-light-color": "#4fc3f7",
    },
  },
  tropicalBreeze: {
    palette: {
      primary: { main: "#00897b" }, // Teal
      secondary: { main: "#ff7043" }, // Coral
      error: { main: "#e53935" }, // Red
      background: { default: "#e0f7fa" }, // Light Teal
      text: { primary: "#004d40", secondary: "#00796b" },
    },
    cssVars: {
      "--primary-color": "#00897b",
      "--primary-gradient": "linear-gradient(90deg, #00897b, #ff7043)",
      "--secondary-color": "#ff7043",
      "--accent-color-1": "#4db6ac",
      "--accent-color-2": "#b2dfdb",
      "--background-color": "#e0f7fa",
      "--sidebar-bg-color": "#ffffff",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #ffffff, #a7ffeb)",
      "--paper-color": "#ffffff",
      "--text-primary-color": "#004d40",
      "--text-secondary-color": "#00796b",
      "--button-text-transform": "none",
      "--error-color": "#e53935",
      "--button-hover-color": "#00695c",
      "--primary-light-color": "#80cbc4",
    },
  },
  tropicalBreeze: {
    palette: {
      primary: { main: "#00897b" }, // Teal
      secondary: { main: "#ff7043" }, // Coral
      error: { main: "#e53935" }, // Red
      background: { default: "#e0f7fa" }, // Light Teal
      text: { primary: "#004d40", secondary: "#00796b" },
    },
    cssVars: {
      "--primary-color": "#00897b",
      "--primary-gradient": "linear-gradient(90deg, #00897b, #ff7043)",
      "--secondary-color": "#ff7043",
      "--accent-color-1": "#4db6ac",
      "--accent-color-2": "#b2dfdb",
      "--background-color": "#e0f7fa",
      "--sidebar-bg-color": "#ffffff",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #ffffff, #a7ffeb)",
      "--paper-color": "#ffffff",
      "--text-primary-color": "#004d40",
      "--text-secondary-color": "#00796b",
      "--button-text-transform": "none",
      "--error-color": "#e53935",
      "--button-hover-color": "#00695c",
      "--primary-light-color": "#80cbc4",
    },
  },
  midnightShadow: {
    palette: {
      primary: { main: "#263238" }, // Dark Blue Grey
      secondary: { main: "#37474f" }, // Blue Grey
      error: { main: "#d32f2f" }, // Red
      background: { default: "#212121" }, // Very Dark Grey
      text: { primary: "#ffffff", secondary: "#b0bec5" },
    },
    cssVars: {
      "--primary-color": "#263238",
      "--primary-gradient": "linear-gradient(90deg, #263238, #37474f)",
      "--secondary-color": "#37474f",
      "--accent-color-1": "#546e7a",
      "--accent-color-2": "#78909c",
      "--background-color": "#212121",
      "--sidebar-bg-color": "#263238",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #263238, #4f5b62)",
      "--paper-color": "#37474f",
      "--text-primary-color": "#ffffff",
      "--text-secondary-color": "#b0bec5",
      "--button-text-transform": "none",
      "--error-color": "#d32f2f",
      "--button-hover-color": "#1c313a",
      "--primary-light-color": "#90a4ae",
    },
  },
  royalBurgundy: {
    palette: {
      primary: { main: "#880e4f" }, // Burgundy
      secondary: { main: "#6a1b9a" }, // Purple
      error: { main: "#d32f2f" }, // Red
      background: { default: "#212121" }, // Very Dark Grey
      text: { primary: "#ffffff", secondary: "#f50057" },
    },
    cssVars: {
      "--primary-color": "#880e4f",
      "--primary-gradient": "linear-gradient(90deg, #880e4f, #6a1b9a)",
      "--secondary-color": "#6a1b9a",
      "--accent-color-1": "#9c4dcc",
      "--accent-color-2": "#f8bbd0",
      "--background-color": "#212121",
      "--sidebar-bg-color": "#2c2c2c",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #2c2c2c, #6a1b9a)",
      "--paper-color": "#292929",
      "--text-primary-color": "#ffffff",
      "--text-secondary-color": "#f50057",
      "--button-text-transform": "none",
      "--error-color": "#d32f2f",
      "--button-hover-color": "#6a1b9a",
      "--primary-light-color": "#c2185b",
    },
  },
  forestNight: {
    palette: {
      primary: { main: "#004d40" }, // Dark Teal
      secondary: { main: "#00796b" }, // Forest Green
      error: { main: "#d32f2f" }, // Red
      background: { default: "#263238" }, // Dark Grey
      text: { primary: "#ffffff", secondary: "#80cbc4" },
    },
    cssVars: {
      "--primary-color": "#004d40",
      "--primary-gradient": "linear-gradient(90deg, #004d40, #00796b)",
      "--secondary-color": "#00796b",
      "--accent-color-1": "#26a69a",
      "--accent-color-2": "#b2dfdb",
      "--background-color": "#263238",
      "--sidebar-bg-color": "#2f3e46",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #2f3e46, #004d40)",
      "--paper-color": "#37474f",
      "--text-primary-color": "#ffffff",
      "--text-secondary-color": "#80cbc4",
      "--button-text-transform": "none",
      "--error-color": "#d32f2f",
      "--button-hover-color": "#003c33",
      "--primary-light-color": "#00796b",
    },
  },
  charcoalSlate: {
    palette: {
      primary: { main: "#212121" }, // Charcoal Grey
      secondary: { main: "#424242" }, // Slate Grey
      error: { main: "#e53935" }, // Red
      background: { default: "#303030" }, // Very Dark Grey
      text: { primary: "#ffffff", secondary: "#eeeeee" },
    },
    cssVars: {
      "--primary-color": "#212121",
      "--primary-gradient": "linear-gradient(90deg, #212121, #424242)",
      "--secondary-color": "#424242",
      "--accent-color-1": "#616161",
      "--accent-color-2": "#757575",
      "--background-color": "#303030",
      "--sidebar-bg-color": "#424242",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #424242, #616161)",
      "--paper-color": "#424242",
      "--text-primary-color": "#ffffff",
      "--text-secondary-color": "#eeeeee",
      "--button-text-transform": "none",
      "--error-color": "#e53935",
      "--button-hover-color": "#b71c1c",
      "--primary-light-color": "#757575",
    },
  },
  obsidianGlow: {
    palette: {
      primary: { main: "#263238" }, // Dark Obsidian
      secondary: { main: "#4f5b62" }, // Steel Blue
      error: { main: "#f44336" }, // Red
      background: { default: "#37474f" }, // Dark Blue Grey
      text: { primary: "#ffffff", secondary: "#90a4ae" },
    },
    cssVars: {
      "--primary-color": "#263238",
      "--primary-gradient": "linear-gradient(90deg, #263238, #4f5b62)",
      "--secondary-color": "#4f5b62",
      "--accent-color-1": "#78909c",
      "--accent-color-2": "#b0bec5",
      "--background-color": "#37474f",
      "--sidebar-bg-color": "#2c3e50",
      "--sidebar-bg-gradient": "linear-gradient(135deg, #2c3e50, #4f5b62)",
      "--paper-color": "#37474f",
      "--text-primary-color": "#ffffff",
      "--text-secondary-color": "#90a4ae",
      "--button-text-transform": "none",
      "--error-color": "#f44336",
      "--button-hover-color": "#1c313a",
      "--primary-light-color": "#546e7a",
    },
  },
};

export const ThemeContext = createContext();

export const ThemeContextProvider = ({ children }) => {
  const [themeName, setThemeName] = useState("light");

  const theme = useMemo(() => {
    const selectedTheme = themes[themeName];
    Object.entries(selectedTheme.cssVars).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
    return createTheme(selectedTheme);
  }, [themeName]);

  const changeTheme = (newTheme) => setThemeName(newTheme);

  return (
    <ThemeContext.Provider value={{ themeName, changeTheme }}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};
